import React, { useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiClient, Graph } from '@/lib/api'
import { ModernOSINTDashboard } from '@/components/ModernOSINTDashboard'
import { useAuth } from '@/contexts/AuthContext'
import { Clock, Search, Zap, Mail, Phone, User, Globe, Building2, Hash } from 'lucide-react'
import { QueryTypeIndicator } from '@/components/breach/QueryTypeIndicator'

interface HistoricalSearch {
  id: string
  timestamp: string
  query?: string
  status: string
  results_count?: number
}

/** Caller ID + Facebook data from Eyecon (phone lookups only) */
export interface CallerIdData {
  status: boolean
  message?: string
  data?: {
    fullName?: string
    b64?: string
    otherNames?: Array<{ name: string; type: string }>
    facebookID?: Record<string, unknown>
    images?: Array<{
      id?: string
      pictures?: { '200'?: string; '600'?: string }
    }>
  }
}

/** Parse query into country_code + number if it looks like a phone number. */
function parsePhoneQuery(q: string): { countryCode: string; number: string } | null {
  const s = (q || '').trim().replace(/\s+/g, '').replace(/-/g, '')
  const digits = s.replace(/\D/g, '')
  if (digits.length < 10) return null
  if (digits.length === 10) return { countryCode: '91', number: digits }
  if (digits.length === 11 && digits.startsWith('0')) return { countryCode: '91', number: digits.slice(1) }
  if (digits.length >= 11 && digits.startsWith('91')) return { countryCode: '91', number: digits.slice(2) }
  if (digits.length >= 12) return { countryCode: digits.slice(0, -10), number: digits.slice(-10) }
  return null
}

const CAPABILITY_CARDS: Array<{
  type: string
  Icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  sources: string[]
  example: string
  hex: string
}> = [
  {
    type: 'email',
    Icon: Mail,
    title: 'Email Search',
    description: 'Breach databases, Gmail OSINT, professional profiles, corruption screening',
    sources: ['LeakOSINT', 'Gmail OSINT', 'RocketReach', 'OCCRP', 'Hudson Rock'],
    example: 'john@example.com',
    hex: '#3b82f6',
  },
  {
    type: 'phone',
    Icon: Phone,
    title: 'Phone Lookup',
    description: 'Caller ID, Facebook reverse lookup, breach database search',
    sources: ['Eyecon', 'Facebook', 'LeakOSINT'],
    example: '+91 9876543210',
    hex: '#14b8a6',
  },
  {
    type: 'username',
    Icon: User,
    title: 'Username Search',
    description: 'Breach databases, stealer logs, web footprint analysis',
    sources: ['LeakOSINT', 'Hudson Rock', 'OCCRP', 'Google'],
    example: 'johndoe123',
    hex: '#22c55e',
  },
  {
    type: 'domain',
    Icon: Globe,
    title: 'Domain Intel',
    description: 'Infrastructure scanning, exposed services, data leak detection',
    sources: ['LeakIX', 'Hudson Rock', 'OCCRP', 'Google'],
    example: 'example.com',
    hex: '#06b6d4',
  },
  {
    type: 'fullName',
    Icon: Search,
    title: 'Full Name Search',
    description: 'Breach lookup, professional enrichment, sanctions & corruption screening',
    sources: ['LeakOSINT', 'RocketReach', 'OCCRP', 'Google'],
    example: 'John Doe',
    hex: '#a855f7',
  },
  {
    type: 'govtId',
    Icon: Hash,
    title: 'Govt ID Search',
    description: 'Search SSN, passport numbers, national IDs in breach databases',
    sources: ['LeakOSINT', 'OCCRP'],
    example: '123-45-6789',
    hex: '#f59e0b',
  },
  {
    type: 'company',
    Icon: Building2,
    title: 'Company Investigation',
    description: 'Sanctions screening, corruption data, domain infrastructure, web footprint',
    sources: ['OCCRP Aleph', 'LeakIX', 'Google'],
    example: 'Acme Corp Inc',
    hex: '#f97316',
  },
]

export default function BreachINT() {
  const { refreshUser, user } = useAuth()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Graph | null>(null)
  const [callerIdData, setCallerIdData] = useState<CallerIdData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking')
  const [cachedData, setCachedData] = useState<Map<string, Graph>>(new Map())
  const [historicalSearches, setHistoricalSearches] = useState<HistoricalSearch[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [showHistory, setShowHistory] = useState(true)


  const fetchCallerIdIfPhone = async (searchQuery: string) => {
    const parsed = parsePhoneQuery(searchQuery)
    if (!parsed) {
      setCallerIdData(null)
      return
    }
    try {
      const res = await apiClient.getCallerId(parsed.countryCode, parsed.number) as CallerIdData
      setCallerIdData(res)
    } catch {
      setCallerIdData(null)
    }
  }

  useEffect(() => {
    const checkBackendStatus = async () => {
      try {
        await apiClient.healthCheck()
        setBackendStatus('online')
      } catch {
        setBackendStatus('offline')
      }
    }
    checkBackendStatus()
  }, [])

  useEffect(() => {
    const loadHistoricalSearches = async () => {
      if (!user?.id) return
      setLoadingHistory(true)
      try {
        const response = await apiClient.getUserQueryHistory(user.id, { limit: 20 }) as any
        const queries = response.queries || response.logs || []
        const breachQueries = queries.filter((q: any) =>
          q.endpoint === '/breach/search' && q.query
        )
        setHistoricalSearches(breachQueries)
      } catch (error) {
        console.error('Failed to load historical searches:', error)
      } finally {
        setLoadingHistory(false)
      }
    }
    if (user?.id && !results) {
      loadHistoricalSearches()
    }
  }, [user?.id, results])

  const loadFromHistory = async (searchQuery: string) => {
    setQuery(searchQuery)
    setShowHistory(false)
    setLoading(true)
    try {
      const result = await apiClient.searchBreaches(searchQuery) as Graph
      setResults(result)
      setError(null)
      const cacheKey = `${searchQuery.toLowerCase()}_leakosint`
      setCachedData(prev => new Map(prev).set(cacheKey, result))
      await fetchCallerIdIfPhone(searchQuery)
    } catch (err: any) {
      console.error('Failed to load from history:', err)
      setError(err.message || 'Failed to load search results')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!query.trim()) return
    setShowHistory(false)

    const cacheKey = `${query.toLowerCase()}_leakosint`
    if (cachedData.has(cacheKey)) {
      setResults(cachedData.get(cacheKey)!)
      fetchCallerIdIfPhone(query)
      return
    }

    setLoading(true)
    setError(null)
    try {
      try {
        await apiClient.healthCheck()
      } catch {
        throw new Error('Backend API server is not running. Please start the backend server first.')
      }
      const finalResults = await apiClient.searchBreaches(query) as Graph
      if (!finalResults || !finalResults.nodes || finalResults.nodes.length === 0) {
        throw new Error('No results found')
      }
      setResults(finalResults)
      setCachedData(prev => new Map(prev).set(cacheKey, finalResults))
      await fetchCallerIdIfPhone(query)
      refreshUser().catch((err: any) => console.error('Failed to refresh user data:', err))
    } catch (error) {
      console.error('Breach search failed:', error)
      let errorMessage = 'Search failed'
      if (error instanceof Error) {
        if (error.message.includes('Backend API server is not running')) {
          errorMessage = 'Backend API server is not running. Please start the backend server using the start_backend_server.bat file.'
        } else if (error.message.includes('500')) {
          errorMessage = 'Backend server error. Please check if the Elasticsearch connection is working.'
        } else if (error.message.includes('401')) {
          errorMessage = 'Authentication failed. Please log in again.'
        } else {
          errorMessage = error.message
        }
      }
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleRerunSearch = async (searchQuery: string, forceRefresh: boolean = false) => {
    setShowHistory(false)
    setLoading(true)
    setError(null)
    if (forceRefresh) {
      const cacheKey = `${searchQuery.toLowerCase()}_leakosint`
      setCachedData(prev => {
        const newCache = new Map(prev)
        newCache.delete(cacheKey)
        return newCache
      })
    }
    try {
      try {
        await apiClient.healthCheck()
      } catch {
        throw new Error('Backend API server is not running. Please start the backend server first.')
      }
      const finalResults = await apiClient.searchBreaches(searchQuery, forceRefresh) as Graph
      if (!finalResults || !finalResults.nodes || finalResults.nodes.length === 0) {
        throw new Error('No results found')
      }
      setResults(finalResults)
      setQuery(searchQuery)
      const cacheKey = `${searchQuery.toLowerCase()}_leakosint`
      setCachedData(prev => new Map(prev).set(cacheKey, finalResults))
      await fetchCallerIdIfPhone(searchQuery)
      refreshUser().catch((err: any) => console.error('Failed to refresh user data:', err))
    } catch (error) {
      console.error('Re-run search failed:', error)
      let errorMessage = 'Re-run search failed'
      if (error instanceof Error) {
        if (error.message.includes('Backend API server is not running')) {
          errorMessage = 'Backend API server is not running. Please start the backend server using the start_backend_server.bat file.'
        } else if (error.message.includes('500')) {
          errorMessage = 'Backend server error. Please check if the Elasticsearch connection is working.'
        } else if (error.message.includes('401')) {
          errorMessage = 'Authentication failed. Please log in again.'
        } else {
          errorMessage = error.message
        }
      }
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Show results dashboard when available
  if (results) {
    return (
      <ModernOSINTDashboard
        graph={results}
        query={query}
        onRerunSearch={handleRerunSearch}
        callerIdData={callerIdData}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-900 to-slate-950">
      <div className="container mx-auto px-4 py-8 space-y-8 max-w-5xl">
        {/* Header */}
        <div className="text-center space-y-4 pt-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
            <span className="text-xs text-red-400 font-medium tracking-wide">Sycek Profiler</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white">
            Intelligence Profiler
          </h1>

          <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Multi-source OSINT investigation across breach databases, stealer logs, professional profiles,
            sanctions lists, infrastructure scans, and social media. AI-powered threat assessment included.
          </p>

          {/* Backend Status */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-black/40 border border-gray-700/50 rounded-lg">
            <div className={`w-2 h-2 rounded-full ${
              backendStatus === 'online' ? 'bg-green-500 animate-pulse' :
              backendStatus === 'offline' ? 'bg-red-500' :
              'bg-yellow-500 animate-pulse'
            }`} />
            <span className="text-xs font-mono text-gray-400">
              {backendStatus === 'online' ? 'API Connected' :
               backendStatus === 'offline' ? 'API Disconnected' : 'Connecting...'}
            </span>
          </div>
        </div>

        {/* Search Section */}
        <div className="bg-black/40 border border-gray-700/50 rounded-xl p-6 backdrop-blur-sm">
          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-500" />
              </div>
              <Input
                className="pl-12 h-14 text-lg bg-black/60 border-2 border-gray-600/50 focus:border-red-500 text-white placeholder:text-gray-500 rounded-lg font-mono"
                placeholder="Enter email, username, domain, phone, name, or ID..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button
                onClick={handleSearch}
                disabled={loading || !query.trim()}
                className="absolute right-2 top-2 h-10 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white border-0 font-mono"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Searching...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Investigate
                  </div>
                )}
              </Button>
            </div>

            {/* Query Type Detection + Source Preview */}
            {query.trim() && (
              <div className="pt-1">
                <QueryTypeIndicator query={query} showSources={true} />
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-red-300">Search Error</h3>
                <p className="text-red-400/80 mt-1 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Capability Showcase */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-gray-400 font-mono text-sm tracking-wider">INVESTIGATION CAPABILITIES</span>
            <div className="flex-1 h-px bg-gray-700/50" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {CAPABILITY_CARDS.map((cap) => {
              const Icon = cap.Icon
              return (
                <button
                  key={cap.type}
                  onClick={() => {
                    setQuery(cap.example)
                    const input = document.querySelector('input[type="text"]') as HTMLInputElement
                    input?.focus()
                  }}
                  className="text-left p-4 rounded-xl bg-white/[0.03] border border-white/[0.07] hover:border-white/[0.14] hover:bg-white/[0.05] transition-all active:scale-[0.98] group"
                  style={{ borderLeftColor: cap.hex, borderLeftWidth: '3px' }}
                >
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${cap.hex}18`, border: `1px solid ${cap.hex}30`, color: cap.hex }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-sm font-semibold text-white group-hover:text-green-400 transition-colors">{cap.title}</span>
                  </div>
                  <p className="text-slate-500 text-xs leading-relaxed mb-3">{cap.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {cap.sources.map((src) => (
                      <span key={src} className="text-[10px] text-slate-600 bg-white/[0.04] px-1.5 py-0.5 rounded">
                        {src}
                      </span>
                    ))}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Recent Searches */}
        {showHistory && historicalSearches.length > 0 && (
          <div className="bg-black/40 border border-gray-700/50 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700/50">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="font-mono text-sm text-gray-400">RECENT SEARCHES</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(false)}
                className="text-gray-500 h-7 text-xs"
              >
                Hide
              </Button>
            </div>
            <div className="divide-y divide-gray-800/50">
              {loadingHistory ? (
                <div className="text-center py-8 text-gray-500 text-sm">Loading history...</div>
              ) : (
                historicalSearches.slice(0, 8).map((search) => (
                  <div
                    key={search.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-800/30 cursor-pointer transition-colors group"
                    onClick={() => search.query && loadFromHistory(search.query)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-7 h-7 bg-red-900/20 border border-red-500/20 rounded flex items-center justify-center flex-shrink-0">
                        <Search className="w-3 h-3 text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-gray-200 truncate">
                          {search.query || 'Unknown query'}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-600">
                            {new Date(search.timestamp).toLocaleString()}
                          </span>
                          {search.results_count !== undefined && (
                            <Badge variant="secondary" className="text-[10px] bg-gray-800 text-gray-400 border-0">
                              {search.results_count} results
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Search className="w-4 h-4 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
